/**
 * Color utility for generating accent palettes from a single hex color.
 * Used by the brand profile feature to create CSS custom properties.
 */

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace("#", "");
  return {
    r: parseInt(clean.substring(0, 2), 16),
    g: parseInt(clean.substring(2, 4), 16),
    b: parseInt(clean.substring(4, 6), 16),
  };
}

export function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360;
  s /= 100;
  l /= 100;

  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  };
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

export interface AccentPalette {
  "300": string;
  "400": string;
  "500": string;
  "600": string;
  "600-20": string;
  "600-30": string;
  "500-15": string;
  "500-30": string;
  "500-50": string;
}

/**
 * Generate a full accent palette from a single hex color.
 * The input is treated as the 600 shade (the "base" accent).
 * Lighter shades are derived by increasing lightness in HSL space.
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b);
}

export function hslToHex(h: number, s: number, l: number): string {
  const { r, g, b } = hslToRgb(h, s, l);
  return rgbToHex(r, g, b);
}

export function generateGlowShadow(
  hex: string,
  intensity: "subtle" | "medium" | "strong"
): string {
  const { r, g, b } = hexToRgb(hex);
  const configs = {
    subtle: `0 0 8px rgba(${r},${g},${b},0.3), 0 0 16px rgba(${r},${g},${b},0.1)`,
    medium: `0 0 12px rgba(${r},${g},${b},0.4), 0 0 24px rgba(${r},${g},${b},0.2)`,
    strong: `0 0 16px rgba(${r},${g},${b},0.5), 0 0 32px rgba(${r},${g},${b},0.3), 0 0 48px rgba(${r},${g},${b},0.1)`,
  };
  return configs[intensity];
}

export function generateAccentPalette(hex: string): AccentPalette {
  const { r, g, b } = hexToRgb(hex);
  const hsl = rgbToHsl(r, g, b);

  // 600 = input color
  const rgb600 = { r, g, b };
  const hex600 = hex.startsWith("#") ? hex : `#${hex}`;

  // 500 = slightly lighter
  const rgb500 = hslToRgb(hsl.h, Math.min(hsl.s + 5, 100), Math.min(hsl.l + 10, 95));
  const hex500 = rgbToHex(rgb500.r, rgb500.g, rgb500.b);

  // 400 = lighter still
  const rgb400 = hslToRgb(hsl.h, Math.min(hsl.s + 5, 100), Math.min(hsl.l + 22, 95));
  const hex400 = rgbToHex(rgb400.r, rgb400.g, rgb400.b);

  // 300 = lightest
  const rgb300 = hslToRgb(hsl.h, Math.min(hsl.s + 5, 100), Math.min(hsl.l + 34, 95));
  const hex300 = rgbToHex(rgb300.r, rgb300.g, rgb300.b);

  return {
    "300": hex300,
    "400": hex400,
    "500": hex500,
    "600": hex600,
    "600-20": `rgba(${rgb600.r}, ${rgb600.g}, ${rgb600.b}, 0.2)`,
    "600-30": `rgba(${rgb600.r}, ${rgb600.g}, ${rgb600.b}, 0.3)`,
    "500-15": `rgba(${rgb500.r}, ${rgb500.g}, ${rgb500.b}, 0.15)`,
    "500-30": `rgba(${rgb500.r}, ${rgb500.g}, ${rgb500.b}, 0.3)`,
    "500-50": `rgba(${rgb500.r}, ${rgb500.g}, ${rgb500.b}, 0.5)`,
  };
}
